import {
  FetchFilterPayload,
  FiltersData,
  FiltersResponse
} from '@model/iceberg/service/price-calendar/filters-response';
import { Filters, PriceCalendarFiltersState } from '@model/state/price-calendar-state';
import {
  DealFinderResultsActions,
  setFilters as dealFinderSetFilters
} from '@state/slices/deal-finder-results/dealFinderResultsSlice';
import { getShowModalPayload, ModalType, showModal } from '@state/slices/modal/modalSlice';
import { PriceCalendarApi } from '@model/iceberg/service/price-calendar';
import { PriceCalendarParams } from '@model/price-calendar';
import { identity, isEmpty, isEqual, pick, pickBy } from 'lodash';
import { isValidSearchDate } from '@util/date-time';
import { getErrorCode } from '@util/error';
import { PriceCalendarFilterActions } from '@state/slices/price-calendar/priceCalendarActions';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@state/store';

export const INITIAL_PRICE_CALENDAR_FILTERS_STATE: PriceCalendarFiltersState = {
  response: {
    data: {
      filters: {
        durations: [],
        boards: [],
        airports: []
      },
      defaults: {
        month: '',
        year: '',
        from: '',
        occupancy: [],
        boardBasis: '',
        duration: 0
      }
    },
    loading: false,
    error: null
  },
  airport: '',
  board: '',
  duration: 0,
  occupancy: [{ adults: 2, children: [] }],
  lastGood: null
};

interface LastGoodFiltersState extends PriceCalendarFiltersState {
  month: number;
  year: number;
}

export const fetchFilters = createAsyncThunk<
  FiltersData,
  FetchFilterPayload,
  { state: RootState; rejectValue: string }
>(PriceCalendarFilterActions.FETCH_FILTERS, async ({ path, urlParams }: any, { dispatch, rejectWithValue }) => {
  try {
    const { month, year, ...nonDateParams } = urlParams || {};
    const searchParams =
      isNaN(month) ||
      isNaN(year) ||
      !isValidSearchDate({
        month,
        year
      })
        ? nonDateParams
        : urlParams;
    const response: FiltersResponse = await new PriceCalendarApi().filters(path, searchParams);
    if (response && response.data) {
      return response.data;
    } else {
      return rejectWithValue('No data received');
    }
  } catch (error: any) {
    const errorCode = getErrorCode(error);
    if (errorCode) {
      dispatch(showModal(getShowModalPayload(ModalType.GENERIC_ERROR, errorCode)));
    }
    return rejectWithValue(error.message || 'An error occurred');
  }
});

export const getFilters = (params: PriceCalendarParams) => {
  if (!params) return undefined;
  const { from, boardBasis, duration, occupancy, year, month } = params;
  return {
    duration,
    airport: from,
    board: boardBasis,
    occupancy,
    year,
    month
  };
};

const getLastKnownGoodState = (lastGood: LastGoodFiltersState) => {
  if (!lastGood) {
    return INITIAL_PRICE_CALENDAR_FILTERS_STATE;
  }
  return {
    ...INITIAL_PRICE_CALENDAR_FILTERS_STATE,
    ...lastGood,
    lastGood: JSON.stringify(lastGood),
    response: {
      ...INITIAL_PRICE_CALENDAR_FILTERS_STATE.response,
      ...(lastGood.response || {}),
      data: {
        ...lastGood.response?.data,
        defaults: {
          ...lastGood.response?.data?.defaults,
          month: lastGood.month,
          year: lastGood.year
        }
      },
      loading: false,
      error: false
    }
  };
};

const priceCalendarFilterSlice = createSlice({
  name: PriceCalendarFilterActions.SLICE_NAME,
  initialState: INITIAL_PRICE_CALENDAR_FILTERS_STATE,
  reducers: {
    setFilters(state, action: PayloadAction<Filters>) {
      const { airport, board, duration, occupancy } = action.payload;
      state.airport = airport;
      state.board = board;
      state.duration = duration;
      state.occupancy = occupancy;
      state.response.error = null;
    },
    setAirport: (state, action: PayloadAction<string>) => {
      state.airport = action.payload;
    },
    clearResults: (state) => {
      return { ...INITIAL_PRICE_CALENDAR_FILTERS_STATE, lastGood: state.lastGood };
    },
    resetFilters: () => INITIAL_PRICE_CALENDAR_FILTERS_STATE,
    setDefaultFilters() {
      return INITIAL_PRICE_CALENDAR_FILTERS_STATE;
    },
    clearLastGood: (state) => {
      return {
        ...state,
        lastGood: null
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFilters.pending, (state, action) => {
        const { urlParams } = action.meta.arg;
        const stateIsDefault = isEqual(pickBy(state, identity), pickBy(INITIAL_PRICE_CALENDAR_FILTERS_STATE, identity));
        const currentState = stateIsDefault
          ? null
          : pick(state, ['airport', 'board', 'duration', 'month', 'occupancy', 'response']);

        const filters = isEmpty(urlParams) ? null : getFilters(urlParams);
        const sendState = !stateIsDefault || !!filters ? { ...(currentState || {}), ...(filters || {}) } : null;

        if (sendState) {
          Object.assign(state, sendState);
        }

        state.lastGood = sendState ? JSON.stringify(sendState) : null;
        state.response = {
          ...INITIAL_PRICE_CALENDAR_FILTERS_STATE.response,
          loading: true,
          error: null
        };
      })
      .addCase(fetchFilters.fulfilled, (state, action: any) => {
        const { defaults } = action.payload;
        const filters = getFilters(defaults);
        const receiveState = {
          ...pick(state, ['airport', 'board', 'duration', 'month', 'occupancy']),
          ...filters,
          response: {
            data: action.payload,
            loading: false,
            error: null
          }
        };
        Object.assign(state, receiveState);
        state.lastGood = JSON.stringify(receiveState || null);
      })
      .addCase(fetchFilters.rejected, (state, action) => {
        const lastGood = state.lastGood ? JSON.parse(state.lastGood) : null;
        const lastGoodState = getLastKnownGoodState(lastGood);
        Object.assign(state, lastGoodState);
        state.response.error = action.payload || 'An error occurred';
        state.response.loading = false;
      })
      .addCase(DealFinderResultsActions.CLEAR_RESULTS, (state) => {
        return { ...INITIAL_PRICE_CALENDAR_FILTERS_STATE, lastGood: state.lastGood };
      })
      .addCase(dealFinderSetFilters.fulfilled, () => INITIAL_PRICE_CALENDAR_FILTERS_STATE)
      .addCase(DealFinderResultsActions.RECEIVE_RESULTS_FAILURE, (state) => {
        const lastGood = state.lastGood ? JSON.parse(state.lastGood) : null;
        const lastGoodState = getLastKnownGoodState(lastGood);
        Object.assign(state, lastGoodState);
      });
  }
});

export const { setFilters, setAirport, clearResults, resetFilters, setDefaultFilters, clearLastGood } =
  priceCalendarFilterSlice.actions;

export const priceCalendarFilterActions = {
  fetchFilters,
  getFilters,
  getLastKnownGoodState,
  setFilters,
  setAirport,
  clearResults,
  resetFilters,
  setDefaultFilters,
  clearLastGood
};
export const priceCalendarFilterReducer = priceCalendarFilterSlice.reducer;
